import React, { useState, useRef } from 'react';
import {
  UploadCloud,
  FileArchive,
  CheckCircle2,
  AlertTriangle,
  FolderKanban,
  Layers,
  ArrowUp,
  ArrowDown,
  Trash2,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  FileText,
  Image as ImageIcon,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { EduModal } from './EduModal.js';
import { EduButton } from './EduButton.js';
import { EduBadge } from './EduBadge.js';
import { api } from '../services/api.js';
import {
  Project,
  BatchImportAnalyzeResult,
  BatchImportLessonPreview,
} from '../types/index.js';

interface BatchImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  projects: Project[];
  initialProjectId?: string;
  onSuccess?: (projectId: string) => void;
}

export const BatchImportModal: React.FC<BatchImportModalProps> = ({
  isOpen,
  onClose,
  projects,
  initialProjectId,
  onSuccess,
}) => {
  const [step, setStep] = useState<'upload' | 'review' | 'success'>('upload');
  const [selectedProjectId, setSelectedProjectId] = useState<string>(
    initialProjectId || (projects[0]?.id ?? '')
  );

  // Upload State
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Review State
  const [analysisData, setAnalysisData] = useState<BatchImportAnalyzeResult | null>(null);
  const [editableLessons, setEditableLessons] = useState<BatchImportLessonPreview[]>([]);
  const [expandedLessonId, setExpandedLessonId] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  // Success State
  const [createdCount, setCreatedCount] = useState(0);

  // Reset when opened
  React.useEffect(() => {
    if (isOpen) {
      if (initialProjectId) {
        setSelectedProjectId(initialProjectId);
      } else if (!selectedProjectId && projects.length > 0) {
        setSelectedProjectId(projects[0].id);
      }
      setStep('upload');
      setFile(null);
      setAnalysisData(null);
      setEditableLessons([]);
      setAnalysisError(null);
      setConfirmError(null);
      setExpandedLessonId(null);
    }
  }, [isOpen, initialProjectId, projects]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.toLowerCase().endsWith('.zip')) {
        setFile(droppedFile);
        setAnalysisError(null);
      } else {
        setAnalysisError('Por favor, selecione um arquivo no formato .ZIP.');
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selected = e.target.files[0];
      if (selected.name.toLowerCase().endsWith('.zip')) {
        setFile(selected);
        setAnalysisError(null);
      } else {
        setAnalysisError('Por favor, selecione um arquivo no formato .ZIP.');
      }
    }
  };

  const handleAnalyze = async () => {
    if (!selectedProjectId) {
      setAnalysisError('Por favor, selecione o projeto de destino para as aulas.');
      return;
    }
    if (!file) {
      setAnalysisError('Selecione um arquivo ZIP contendo as pastas das aulas.');
      return;
    }

    setAnalyzing(true);
    setAnalysisError(null);

    try {
      const result = await api.analyzeBatchImport(selectedProjectId, file);
      setAnalysisData(result);
      setEditableLessons(result.lessons);
      setStep('review');
    } catch (err: any) {
      setAnalysisError(err.message || 'Falha ao analisar o arquivo ZIP. Verifique se o formato está correto.');
    } finally {
      setAnalyzing(false);
    }
  };

  // Reorder lessons Up / Down
  const handleMoveLesson = (index: number, direction: 'up' | 'down') => {
    const newLessons = [...editableLessons];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newLessons.length) return;

    const temp = newLessons[index];
    newLessons[index] = newLessons[targetIndex];
    newLessons[targetIndex] = temp;

    // Reassign order
    const updated = newLessons.map((l, idx) => ({ ...l, order: idx + 1 }));
    setEditableLessons(updated);
  };

  // Change lesson title
  const handleTitleChange = (tempId: string, newTitle: string) => {
    setEditableLessons((prev) =>
      prev.map((l) => (l.tempId === tempId ? { ...l, title: newTitle } : l))
    );
  };

  // Remove lesson from batch
  const handleRemoveLesson = (tempId: string) => {
    const filtered = editableLessons.filter((l) => l.tempId !== tempId);
    const reordered = filtered.map((l, idx) => ({ ...l, order: idx + 1 }));
    setEditableLessons(reordered);
    if (expandedLessonId === tempId) {
      setExpandedLessonId(null);
    }
  };

  // Confirm Import
  const handleConfirm = async () => {
    if (!analysisData || editableLessons.length === 0) return;

    setIsConfirming(true);
    setConfirmError(null);

    try {
      const payload = {
        batchId: analysisData.batchId,
        lessons: editableLessons.map((l) => ({
          tempId: l.tempId,
          title: l.title.trim() || l.folderName,
          order: l.order,
        })),
      };

      const res = await api.confirmBatchImport(analysisData.projectId, payload);
      setCreatedCount(res.createdLessonCount);
      setStep('success');
    } catch (err: any) {
      setConfirmError(err.message || 'Erro ao gravar aulas no Firebase. Tente novamente.');
    } finally {
      setIsConfirming(false);
    }
  };

  const handleFinish = () => {
    if (onSuccess && analysisData) {
      onSuccess(analysisData.projectId);
    }
    onClose();
  };

  const currentProject = projects.find((p) => p.id === selectedProjectId);

  return (
    <EduModal
      isOpen={isOpen}
      onClose={() => !analyzing && !isConfirming && onClose()}
      title={
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-orange-50 text-brand-primary border border-orange-200">
            <UploadCloud size={18} />
          </div>
          <span>Importação em Lote de Aulas (ZIP)</span>
        </div>
      }
      subtitle={
        step === 'upload'
          ? 'Envie um arquivo ZIP com as pastas das aulas para organizar automaticamente suas cenas'
          : step === 'review'
          ? 'Revise os títulos, a ordem e as associações de cada aula antes de gravar no sistema'
          : 'Aulas cadastradas e organizadas com sucesso!'
      }
      maxWidth={step === 'review' ? '5xl' : '2xl'}
    >
      {/* ETAPA 1: UPLOAD DO ZIP */}
      {step === 'upload' && (
        <div className="space-y-5">
          {/* Seletor de Projeto */}
          <div>
            <label className="block text-xs font-semibold text-txt-primary mb-1.5">
              Projeto de Destino <span className="text-status-error">*</span>
            </label>
            <div className="relative">
              <FolderKanban
                size={16}
                className="absolute left-3 top-3 text-brand-primary"
              />
              <select
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                disabled={analyzing}
                className="w-full pl-9 pr-3 py-2 text-sm border border-bdr rounded-lg text-txt-primary bg-surface focus:outline-none focus:border-brand-primary font-medium"
              >
                <option value="">Selecione o projeto...</option>
                {projects.map((proj) => (
                  <option key={proj.id} value={proj.id}>
                    {proj.name} ({proj.lessonCount || 0} aulas já cadastradas)
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Área de Dropzone */}
          <div>
            <label className="block text-xs font-semibold text-txt-primary mb-1.5">
              Arquivo ZIP do Curso / Disciplina <span className="text-status-error">*</span>
            </label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => !analyzing && fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                isDragging
                  ? 'border-brand-primary bg-orange-50/50 scale-[1.01]'
                  : file
                  ? 'border-brand-primary/60 bg-surface-secondary/40'
                  : 'border-bdr hover:border-brand-primary hover:bg-surface-secondary/30'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".zip,application/zip,application/x-zip-compressed"
                onChange={handleFileChange}
                className="hidden"
                disabled={analyzing}
              />

              {file ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 rounded-full bg-orange-100 text-brand-primary flex items-center justify-center shadow-xs">
                    <FileArchive size={24} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-txt-primary">{file.name}</p>
                    <p className="text-xs text-txt-secondary mt-0.5">
                      {(file.size / (1024 * 1024)).toFixed(2)} MB • Clique ou arraste para substituir
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <div className="w-12 h-12 rounded-full bg-surface-secondary text-txt-muted flex items-center justify-center border border-bdr">
                    <UploadCloud size={24} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-txt-primary">
                      Clique para selecionar ou arraste o arquivo ZIP aqui
                    </p>
                    <p className="text-xs text-txt-secondary mt-1">
                      Suporte a pacotes compactados contendo múltiplas pastas de aulas
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Guia de Estrutura Esperada */}
          <div className="p-4 bg-surface-secondary rounded-xl border border-bdr text-xs space-y-2">
            <div className="flex items-center gap-1.5 text-txt-primary font-semibold">
              <HelpCircle size={15} className="text-brand-primary" />
              <span>Como estruturar o arquivo ZIP?</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-txt-secondary leading-relaxed pt-1">
              <div>
                <p className="font-medium text-txt-primary mb-0.5">• Pastas de 1º Nível = Aulas:</p>
                <p>
                  Cada pasta representará uma aula. O nome da pasta será o título (ex:{' '}
                  <code className="text-brand-primary bg-surface px-1 py-0.5 rounded border border-bdr">Aula 01 - Introdução</code>).
                </p>
              </div>
              <div>
                <p className="font-medium text-txt-primary mb-0.5">• Conteúdo Didático por Aula:</p>
                <p>
                  • <strong>Slides:</strong> Arquivo <code className="text-brand-primary">slides.pdf</code> ou pasta <code className="text-brand-primary">slides/</code> com imagens.<br />
                  • <strong>Roteiro:</strong> Arquivo <code className="text-brand-primary">.docx</code>, <code className="text-brand-primary">.txt</code>, <code className="text-brand-primary">.csv</code> ou <code className="text-brand-primary">.json</code>.
                </p>
              </div>
            </div>
            <div className="pt-2 border-t border-bdr/60 flex items-center gap-2 text-[11px] text-brand-primary font-semibold">
              <Sparkles size={14} />
              <span>
                Esta importação apenas cadastra aulas e cenas. Nenhum áudio/vídeo é gerado e nenhuma franquia é consumida nesta etapa.
              </span>
            </div>
          </div>

          {/* Mensagem de Erro de Análise */}
          {analysisError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-status-error flex items-start gap-2">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span>{analysisError}</span>
            </div>
          )}

          {/* Ações */}
          <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-bdr">
            <EduButton
              type="button"
              variant="secondary"
              size="sm"
              onClick={onClose}
              disabled={analyzing}
            >
              Cancelar
            </EduButton>
            <EduButton
              type="button"
              variant="primary"
              size="sm"
              onClick={handleAnalyze}
              disabled={!file || !selectedProjectId || analyzing}
              isLoading={analyzing}
              icon={<UploadCloud size={16} />}
            >
              {analyzing ? 'Analisando ZIP e Slides...' : 'Analisar Arquivo ZIP'}
            </EduButton>
          </div>
        </div>
      )}

      {/* ETAPA 2: REVISAR IMPORTAÇÃO */}
      {step === 'review' && analysisData && (
        <div className="space-y-5">
          {/* Header de Metadados do Lote */}
          <div className="bg-surface-secondary border border-bdr rounded-xl p-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-orange-100 text-brand-primary rounded-lg border border-orange-200">
                <FileArchive size={20} />
              </div>
              <div>
                <p className="text-xs text-txt-secondary font-medium">Arquivo Analisado</p>
                <p className="text-sm font-bold text-txt-primary truncate max-w-xs sm:max-w-md">
                  {analysisData.zipFileName}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 text-xs">
              <div className="px-3 py-1.5 rounded-lg bg-surface border border-bdr text-txt-secondary">
                Projeto: <strong className="text-txt-primary">{currentProject?.name || analysisData.projectName}</strong>
              </div>
              <div className="px-3 py-1.5 rounded-lg bg-surface border border-bdr text-txt-secondary">
                Aulas no Lote: <strong className="text-brand-primary">{editableLessons.length}</strong>
              </div>
              <div className="px-3 py-1.5 rounded-lg bg-surface border border-bdr text-txt-secondary">
                Total de Slides: <strong className="text-txt-primary">{editableLessons.reduce((acc, l) => acc + l.slideCount, 0)}</strong>
              </div>
            </div>
          </div>

          {/* Alerta de Divergências */}
          {editableLessons.some((l) => l.inconsistentSceneCount > 0 || l.issues.length > 0) && (
            <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 flex items-start gap-2.5">
              <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold">Aviso de Inconsistências Identificadas</p>
                <p className="mt-0.5 text-amber-800">
                  Algumas aulas possuem quantidade de slides diferente do número de roteiros, ou faltam arquivos de narração.
                  Você pode editar os títulos abaixo, excluir aulas indesejadas e depois ajustar os roteiros no editor de carrossel.
                </p>
              </div>
            </div>
          )}

          {/* Lista de Aulas Identificadas */}
          <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
            {editableLessons.length === 0 ? (
              <div className="p-8 text-center bg-surface-secondary border border-bdr rounded-xl text-txt-muted text-xs">
                Todas as aulas foram removidas deste lote. Clique em "Voltar" para carregar outro arquivo.
              </div>
            ) : (
              editableLessons.map((lesson, idx) => {
                const isExpanded = expandedLessonId === lesson.tempId;
                const hasIssues = lesson.inconsistentSceneCount > 0 || lesson.issues.length > 0;

                return (
                  <div
                    key={lesson.tempId}
                    className={`border rounded-xl transition-all ${
                      hasIssues
                        ? 'border-amber-200 bg-amber-50/20'
                        : 'border-bdr bg-surface'
                    }`}
                  >
                    {/* Linha Principal da Aula */}
                    <div className="p-3.5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
                      {/* Controles de Ordem e Título */}
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {/* Botões Reordenar */}
                        <div className="flex flex-col gap-0.5 shrink-0">
                          <button
                            type="button"
                            onClick={() => handleMoveLesson(idx, 'up')}
                            disabled={idx === 0 || isConfirming}
                            className="p-1 rounded text-txt-muted hover:text-txt-primary hover:bg-surface-secondary disabled:opacity-30 disabled:hover:bg-transparent"
                            title="Mover para cima"
                          >
                            <ArrowUp size={12} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleMoveLesson(idx, 'down')}
                            disabled={idx === editableLessons.length - 1 || isConfirming}
                            className="p-1 rounded text-txt-muted hover:text-txt-primary hover:bg-surface-secondary disabled:opacity-30 disabled:hover:bg-transparent"
                            title="Mover para baixo"
                          >
                            <ArrowDown size={12} />
                          </button>
                        </div>

                        {/* Ordem Numérica */}
                        <span className="w-8 h-8 rounded-lg bg-surface-secondary border border-bdr flex items-center justify-center font-mono font-bold text-xs text-txt-secondary shrink-0">
                          #{lesson.order}
                        </span>

                        {/* Input de Título da Aula */}
                        <div className="flex-1 min-w-0">
                          <input
                            type="text"
                            value={lesson.title}
                            onChange={(e) => handleTitleChange(lesson.tempId, e.target.value)}
                            disabled={isConfirming}
                            placeholder="Título da aula..."
                            className="w-full px-2.5 py-1.5 text-xs font-semibold text-txt-primary bg-surface border border-bdr rounded-lg focus:outline-none focus:border-brand-primary"
                          />
                          <span className="text-[10px] text-txt-muted block truncate mt-0.5 pl-1">
                            Pasta de origem: {lesson.folderName}
                          </span>
                        </div>
                      </div>

                      {/* Métricas e Ações da Aula */}
                      <div className="flex items-center gap-2 self-end md:self-center shrink-0">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-surface-secondary text-txt-secondary text-xs font-medium border border-bdr">
                          <ImageIcon size={12} className="text-brand-primary" />
                          {lesson.slideCount} slides
                        </span>

                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-surface-secondary text-txt-secondary text-xs font-medium border border-bdr">
                          <FileText size={12} className="text-brand-primary" />
                          {lesson.scriptCount} roteiros
                        </span>

                        <EduBadge
                          variant={hasIssues ? 'warning' : 'success'}
                          size="sm"
                          dot
                        >
                          {hasIssues
                            ? `${lesson.validSceneCount} válidas / ${lesson.inconsistentSceneCount} pendentes`
                            : `${lesson.validSceneCount} cenas prontas`}
                        </EduBadge>

                        {/* Ver / Ocultar Cenas */}
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedLessonId(isExpanded ? null : lesson.tempId)
                          }
                          className="p-1.5 rounded-lg text-txt-secondary hover:text-brand-primary hover:bg-orange-50 border border-bdr text-xs font-semibold flex items-center gap-1"
                          title="Inspecionar cenas desta aula"
                        >
                          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>

                        {/* Excluir Aula do Lote */}
                        <button
                          type="button"
                          onClick={() => handleRemoveLesson(lesson.tempId)}
                          disabled={isConfirming}
                          className="p-1.5 rounded-lg text-txt-muted hover:text-status-error hover:bg-red-50 border border-bdr transition-colors"
                          title="Remover aula deste lote"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>

                    {/* Drawer Expansível com as Cenas */}
                    {isExpanded && (
                      <div className="p-4 border-t border-bdr/60 bg-surface-secondary/40 space-y-3">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-bold text-txt-primary flex items-center gap-1.5">
                            <Layers size={13} className="text-brand-primary" />
                            Cenas Identificadas na Aula ({lesson.scenes.length})
                          </p>
                          {lesson.issues.length > 0 && (
                            <span className="text-[11px] text-amber-700 font-medium">
                              {lesson.issues.join(' • ')}
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {lesson.scenes.map((sc) => (
                            <div
                              key={sc.sceneNumber}
                              className={`p-2.5 rounded-lg border bg-surface flex gap-2.5 ${
                                sc.validationStatus === 'valid'
                                  ? 'border-bdr'
                                  : 'border-amber-200 bg-amber-50/10'
                              }`}
                            >
                              {/* Thumbnail Slide */}
                              <div className="w-16 h-12 bg-slate-100 rounded border border-bdr overflow-hidden shrink-0 flex items-center justify-center">
                                {sc.previewImageUrl ? (
                                  <img
                                    src={sc.previewImageUrl}
                                    alt={`Slide ${sc.slideNumber}`}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <ImageIcon size={16} className="text-txt-muted/60" />
                                )}
                              </div>

                              {/* Info Cena */}
                              <div className="flex-1 min-w-0 flex flex-col justify-between">
                                <div className="flex items-center justify-between gap-1">
                                  <span className="font-bold text-[11px] text-txt-primary">
                                    Cena #{sc.sceneNumber}
                                  </span>
                                  <span
                                    className={`text-[9px] font-bold px-1.5 py-0.2 rounded ${
                                      sc.validationStatus === 'valid'
                                        ? 'bg-green-100 text-status-success'
                                        : 'bg-amber-100 text-amber-800'
                                    }`}
                                  >
                                    {sc.validationStatus === 'valid'
                                      ? 'Válida'
                                      : !sc.hasScript
                                      ? 'Sem Roteiro'
                                      : 'Sem Slide'}
                                  </span>
                                </div>
                                <p className="text-[11px] text-txt-secondary line-clamp-2 mt-0.5">
                                  {sc.narration || '(Nenhum roteiro associado)'}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Mensagem de Erro de Confirmação */}
          {confirmError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-status-error flex items-start gap-2">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span>{confirmError}</span>
            </div>
          )}

          {/* Rodapé com Ações */}
          <div className="flex items-center justify-between pt-3 border-t border-bdr">
            <EduButton
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setStep('upload')}
              disabled={isConfirming}
            >
              Voltar / Trocar Arquivo
            </EduButton>

            <div className="flex items-center gap-2">
              <EduButton
                type="button"
                variant="primary"
                size="sm"
                onClick={handleConfirm}
                disabled={editableLessons.length === 0 || isConfirming}
                isLoading={isConfirming}
                icon={<CheckCircle2 size={16} />}
              >
                {isConfirming
                  ? 'Cadastrando Aulas e Cenas no Firebase...'
                  : `Confirmar e Cadastrar ${editableLessons.length} Aulas`}
              </EduButton>
            </div>
          </div>
        </div>
      )}

      {/* ETAPA 3: SUCESSO */}
      {step === 'success' && (
        <div className="py-6 px-2 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-green-50 text-status-success border-2 border-green-200 flex items-center justify-center mx-auto shadow-sm">
            <CheckCircle2 size={36} />
          </div>

          <div>
            <h3 className="text-lg font-bold text-txt-primary">
              Importação Concluída com Sucesso!
            </h3>
            <p className="text-xs text-txt-secondary max-w-md mx-auto mt-1 leading-relaxed">
              Foram cadastradas com sucesso <strong>{createdCount} aulas</strong> e todos os registros
              de suas respectivas cenas no projeto <strong>"{currentProject?.name}"</strong>.
            </p>
          </div>

          <div className="p-4 bg-surface-secondary border border-bdr rounded-xl max-w-lg mx-auto text-left text-xs text-txt-secondary space-y-1.5">
            <p className="font-semibold text-txt-primary flex items-center gap-1.5">
              <Sparkles size={14} className="text-brand-primary" />
              Próximos Passos:
            </p>
            <p>• Suas aulas foram criadas como <strong>Rascunho (Draft)</strong> para você conferir.</p>
            <p>• Você pode clicar em <strong>Editar Cenas</strong> para abrir o editor carrossel, ajustar pausas e roteiros.</p>
            <p>• Nenhuma narração de áudio foi gravada e nenhuma cota foi deduzida da sua franquia.</p>
          </div>

          <div className="pt-3 flex justify-center gap-3">
            <EduButton
              type="button"
              variant="primary"
              size="md"
              onClick={handleFinish}
              icon={<FolderKanban size={16} />}
            >
              Ver Aulas do Projeto
            </EduButton>
          </div>
        </div>
      )}
    </EduModal>
  );
};
